/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Dialog,
    IconButton,
    SwipeableDrawer,
    useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { func, object, string } from 'prop-types';

import { getOfferInboxData, getPageType } from '../../../../../../../state/ducks/App/App-Selectors';
import { setOfferInboxData, setOfferInboxDrawerOpen } from '../../../../../../../state/ducks/App/App-Actions';
import noop from '../../../../../../helpers/noop';
import PopupStructure from './PopupStructure';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import useDialogNoScroll from '../../../../../../helpers/hooks/useDialogNoScroll';

const useStyles = makeStyles((theme) => ({
    drawerBackdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },

    bottomSheetPaper: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        maxHeight: '85vh',
        overflow: 'visible',
    },
    desktopDialogPaper: {
        borderRadius: 12,
        minWidth: '500px',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            padding: 16,
        },
    },

    header: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },

    closeButton: {
        position: 'absolute',
        right: '0',
        top: '0',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },

    content: {
        outline: 'none',
    },
}));

const OfferInboxPopup = ({ instanceId, handleClose, offerData }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
    const offerInbox = useSelector(getOfferInboxData);
    const drawerOpen = offerInbox?.isDrawerOpen?.[instanceId];
    const pageType = useSelector(getPageType);

    useDialogNoScroll(drawerOpen);

    const trackClickThrough = useClickStreamCustomEventsTracking({
        experimentTracking: offerData?.experimentTracking,
        isFireImpression: true,
        page: { type: pageType || 'home' },
    });

    const handleCloseDrawer = () => {
        dispatch(setOfferInboxData({
            usePopup: false,
        }));
        dispatch(setOfferInboxDrawerOpen(null));
        handleClose();
    };

    if (!drawerOpen) {
        return null;
    }

    const structure = offerData?.content?.structure || [];
    const sharedStructure = (
        <PopupStructure
            structure={structure}
            offer={offerData}
            trackClickThrough={trackClickThrough}
        />
    );

    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={!!drawerOpen}
                onClose={handleCloseDrawer}
                onOpen={noop}
                disableSwipeToOpen
                keepMounted={false}
                classes={{
                    paper: classes.bottomSheetPaper,
                }}
                BackdropProps={{
                    classes: { root: classes.drawerBackdrop },
                }}
            >
                <Box className={classes.header}>
                    <IconButton
                        aria-label="close offer inbox"
                        onClick={handleCloseDrawer}
                        className={classes.closeButton}
                        disableRipple
                        disableTouchRipple
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box id="offer-inbox-drawer-content">
                    {sharedStructure}
                </Box>
            </SwipeableDrawer>
        );
    }

    return (
        <Dialog
            open={!!drawerOpen}
            onClose={handleCloseDrawer}
            fullWidth={false}
            maxWidth={false}
            BackdropProps={{
                classes: { root: classes.drawerBackdrop },
            }}
            PaperProps={{
                className: classes.desktopDialogPaper,
            }}
        >
            <Box className={classes.header}>
                <IconButton
                    aria-label="close offer inbox"
                    onClick={handleCloseDrawer}
                    className={classes.closeButton}
                    disableRipple
                    disableTouchRipple
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box className={classes.content} id="offer-inbox-dialog-content">
                {sharedStructure}
            </Box>
        </Dialog>
    );
};

OfferInboxPopup.propTypes = {
    instanceId: string.isRequired,
    handleClose: func,
    offerData: object,
};

OfferInboxPopup.defaultProps = {
    handleClose: noop,
    offerData: {},
};

export default OfferInboxPopup;
